import React, { FC, useCallback } from 'react';
import {
  getAdvancedRowsPerPageOptions,
  getRowsPerPageOptions,
} from '@src/utils';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';
import { selectItemType } from '@src/types';
import SizeChangeSelect from '@uikit/Pagination/SizeChangeSelect';
import { RefreshIcon } from '@src/svg';

interface PaginationProps {
  size?: number;
  totalElements?: number;
  itemsCount: number;
  currentPage: number;
  handlePageChange: (arg: boolean) => void;
  handleSizeChange: (arg: number) => void;
  tableType?: 'regular' | 'advanced';
  onRefresh?: () => void;
  isRefreshing?: boolean;
  dataTestId?: string;
}

const Pagination: FC<PaginationProps> = ({
  size = DEFAULT_PAGE_SIZE,
  totalElements = 0,
  itemsCount,
  currentPage,
  handlePageChange,
  handleSizeChange,
  tableType = 'regular',
  onRefresh,
  isRefreshing,
  dataTestId = 'pagination-component',
}) => {
  const totalPage = Math.max(1, Math.ceil(totalElements / size)) || 1;
  const activePage = Math.min(Math.max(currentPage || 1, 1), totalPage);
  const isSizeChangeDisabled = totalElements === 0;
  const isPreviousDisabled = activePage <= 1;
  const isNextDisabled = activePage >= totalPage;
  const calcEntries = useCallback(() => {
    const entries = itemsCount > totalElements ? totalElements : itemsCount;

    return `${entries} of ${totalElements} entries`;
  }, [itemsCount, totalElements]);

  return (
    <div className="pagination">
      <div className="pagination-entries">
        <div className="pagination-editable-entries">
          <SizeChangeSelect
            countValue={isSizeChangeDisabled ? 0 : size}
            options={
              tableType === 'advanced'
                ? getAdvancedRowsPerPageOptions(totalElements)
                : getRowsPerPageOptions
            }
            disabled={isSizeChangeDisabled}
            onChange={(selectedOption: selectItemType) => {
              handleSizeChange(+selectedOption?.value);
            }}
            dataTestId={`${dataTestId}-size-select`}
          />
          <div>
            of <span>{totalElements}</span> entries
          </div>
          <button
            type="button"
            className={`pagination-refresh ${isRefreshing ? 'is-refreshing' : ''}`}
            onClick={onRefresh}
            disabled={!onRefresh || isSizeChangeDisabled || isRefreshing}
            aria-label="Refresh table"
            title="Refresh"
            data-test-id={`${dataTestId}-refresh`}
          >
            <RefreshIcon />
          </button>
        </div>
      </div>

      <div className="pagination-page_changer">
        <span
          data-test-id={`${dataTestId}-pagination-page-${activePage}`}
          className={isPreviousDisabled ? 'disabled' : ''}
          onClick={() => !isPreviousDisabled && handlePageChange(false)}
        >
          Previous
        </span>

        <div className="pagination-pages">
          {(activePage !== totalPage || totalPage === 1) && (
            <span
              data-test-id={`${dataTestId}-pagination-previous-page`}
              className={activePage === 1 ? 'active' : ''}
              onClick={() => activePage !== 1 && handlePageChange(false)}
            >
              {activePage !== 1 ? activePage - 1 : activePage}
            </span>
          )}
          {totalPage > 1 && (
            <span
              data-test-id={`${dataTestId}-pagination-page-${activePage}`}
              onClick={() => {
                activePage === 1 && handlePageChange(true);
                activePage === totalPage && handlePageChange(false);
              }}
              className={
                activePage !== totalPage && activePage !== 1 ? 'active' : ''
              }
            >
              {activePage === totalPage
                ? activePage - 1
                : activePage === 1
                  ? activePage + 1
                  : activePage}
            </span>
          )}
          {activePage !== 1 && (
            <span
              data-test-id={`${dataTestId}-pagination-page-${activePage}`}
              onClick={() => activePage !== totalPage && handlePageChange(true)}
              className={activePage === totalPage ? 'active' : ''}
            >
              {activePage !== totalPage ? activePage + 1 : activePage}
            </span>
          )}
        </div>

        <span
          className={isNextDisabled ? 'disabled' : ''}
          onClick={() => !isNextDisabled && handlePageChange(true)}
          data-test-id={`${dataTestId}-pagination-next-button`}
        >
          Next
        </span>
      </div>
    </div>
  );
};

export default Pagination;
